import { BigNumber } from 'bignumber.js';
import PropTypes from 'prop-types';

import './SampleRow.css';

import { useMtg } from '../../../hook/useMtg';
import { useStorage } from '../../../hook/useStorage';
import { cardFinish, cardFinishText } from '../../../hoc/MtgProvider';
import { buildClass } from '../../../libs/classes';
import SimpleCheckbox, { CheckboxTypes } from '../../SimpleComponents/SimpleCheckbox';

/**
 * Sample row for sample list component
 * @component
 */
export const SampleRow = ({
	sample,
	isOwned,
	isSelected = false,
	activeSampleId,
	updSampleId,
	cutoffPrice,
	showStorage = true,
	show_checkbox = false,
	onClick,
	onSelect,
}) => {
	const { getStorageTitleShort, getStorageTitleFull } = useStorage();

	const { language, getShortCondition, getFullCondition } = useMtg();

	return (
		<div
			className={buildClass('sample-list-row')
				.add(updSampleId === sample.id, 'updated')
				.add(activeSampleId === sample.id, 'active')}
			onClick={handleClick}
		>
			{show_checkbox ? (
				<SimpleCheckbox state={isSelected} type={CheckboxTypes.BOX} onClick={handleCheckboxClick} />
			) : null}
			{showStorage && isOwned ? (
				<div className="storage_code" title={'Storage: ' + getStorageTitleFull(sample.storage_id)}>
					{getStorageTitleShort(sample.storage_id)}
				</div>
			) : null}
			<div className="card_number" title={getSetTitle()}>
				<span className="set_code">{sample.set ? sample.set.code : '?'}</span>&nbsp;
				<span className="c_number">{sample.card ? sample.card.c_number : '?'}</span>&nbsp;
			</div>
			<div className="language">{getLanguage()}</div>
			<div className="condition">
				<div className={'state ' + getShortCondition(sample)} title={getFullCondition(sample)}>
					{getShortCondition(sample)}
				</div>
			</div>
			<div className="qty">{sample.qty}&#8202;×</div>
			<div className={'name' + (sample.card ? ' rarity-' + sample.card.rarity : '')}>
				{sample.card ? sample.card.name : 'Card Not Found' + console.log(sample)}
			</div>
			<div
				className={'finish finish-' + sample.finish}
				title={sample.finish in cardFinishText ? 'Card is ' + cardFinishText[sample.finish] : ''}
			>
				{sample.finish in cardFinishText ? cardFinishText[sample.finish].charAt(0) : ''}
			</div>
			<div className="note" title={sample.note ? 'Note: ' + sample.note : ''}>
				<span>{sample.note}</span>
			</div>
			{getPriceBlock()}
			{isOwned ? (
				<div className="date_changed" title={'Last change: ' + sample.date_added}>
					{sample.date_changed}
				</div>
			) : null}
		</div>
	);

	function getSetTitle() {
		let language = '';
		try {
			language = language[sample.language].full;
		} catch {
			language = 'Unknown Language';
		}
		return sample.set ? sample.set.name + ' (' + language + ')' : '?';
	}

	function getLanguage() {
		let suffix = '';
		try {
			suffix = language[sample.language].short;
		} catch {
			suffix = '';
		}
		if (suffix) return <span className={'flag flag-' + sample.language} />;
		return <span title="Unknown language">?</span>;
	}

	// Prepare price block
	function getPriceBlock() {
		if (!sample) return <div className="price" />;
		if (!sample.card) return <div className="price">?</div>;

		let price = BigNumber(0);
		switch (sample.finish) {
			case cardFinish.foil:
				price = BigNumber(sample.card.price_usd_foil);
				break;
			case cardFinish.etched:
				price = BigNumber(sample.card.price_usd_etched);
				break;
			case cardFinish.nonfoil:
			default:
				price = price = BigNumber(sample.card.price_usd);
				break;
		}

		if (price.lt(cutoffPrice))
			return (
				<div className="price" title={'Value: $' + BigNumber(price).toFormat(2)}>
					—
				</div>
			);

		let total = price.times(sample.qty);

		return (
			<div className="price">
				<div className="equation" title={'Price of stack of ' + sample.qty + ' card(s)'}>
					${total.toFormat(2)} = {sample.qty}&#8202;×&#8202;
				</div>
				<div className="total" title="Single card price">
					<span className="usd">$&#8202;</span>
					{price.toFormat(2)}
				</div>
			</div>
		);
	}

	function handleClick() {
		onClick(sample);
	}

	function handleCheckboxClick(e) {
		if (onSelect) {
			e.preventDefault();
			onSelect(sample);
		}
	}
};

SampleRow.propTypes = {
	// Displayed sample
	sample: PropTypes.object.isRequired,
	// Is this storage owned by current user
	isOwned: PropTypes.bool.isRequired,
	// Is this sample selected
	isSelected: PropTypes.bool,
	// ID of active sample
	activeSampleId: PropTypes.number.isRequired,
	// ID of last updated sample
	updSampleId: PropTypes.number.isRequired,
	// Cutoff price
	cutoffPrice: PropTypes.number.isRequired,
	// Flag that the storage should be displayed
	showStorage: PropTypes.bool,
	// Flag that selector should be displayed
	show_checkbox: PropTypes.bool,
	// Callback function on click
	onClick: PropTypes.func.isRequired,
	// Callback function on select
	onSelect: PropTypes.func,
};

export default SampleRow;
